/**
 * v-infinite-scroll â€” fires a callback when the user scrolls near the bottom of
 * a scroll container, so any list/table can lazy-load the next page without
 * re-wiring scroll listeners.
 *
 * For an Ant Design Vue table with `:scroll="{ y }"`, the scrollable element is
 * the inner `.ant-table-body`; the directive targets it automatically (falling
 * back to the element itself for plain containers).
 *
 * If a loaded page is too short to overflow the container, no scroll event ever
 * fires; `componentUpdated` detects the non-scrollable state and keeps nudging
 * the callback until the body fills (or the callback stops adding rows).
 *
 * Usage:
 *   <a-table v-infinite-scroll="loadMore" :scroll="{ y: 320 }" />   // default 200px
 *   <a-table v-infinite-scroll:120="loadMore" />                    // custom threshold
 * The callback MUST guard its own `loading` / `hasMore` state (it can be called
 * repeatedly), otherwise the auto-fill could loop.
 */
const DEFAULT_THRESHOLD = 200;

function scrollElOf(el: any): HTMLElement {
	return el.querySelector(".ant-table-body") || el;
}

export default class InfiniteScroll {
	inserted(el: any, binding: any) {
		const state = {
			cb: binding.value,
			threshold: parseInt(binding.arg, 10) || DEFAULT_THRESHOLD
		};
		const scrollEl = scrollElOf(el);
		const handler = () => {
			if (typeof state.cb !== "function") return;
			if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - state.threshold) {
				state.cb();
			}
		};
		scrollEl.addEventListener("scroll", handler);
		el.__infiniteScroll = { scrollEl, handler, state };
	}

	update(el: any, binding: any) {
		if (el.__infiniteScroll) el.__infiniteScroll.state.cb = binding.value;
	}

	componentUpdated(el: any) {
		const s = el.__infiniteScroll;
		if (!s || typeof s.state.cb !== "function") return;
		// Skip while the body is hidden/unsized (clientHeight 0) â€” avoids a spurious
		// load during transitions and a reflow when there's nothing to measure.
		if (s.scrollEl.clientHeight === 0) return;
		// Not enough content to scroll past the threshold â†’ load the next page so
		// the rest of the data isn't stranded behind a missing scrollbar.
		if (s.scrollEl.scrollHeight <= s.scrollEl.clientHeight + s.state.threshold) {
			s.state.cb();
		}
	}

	unbind(el: any) {
		if (el.__infiniteScroll) {
			el.__infiniteScroll.scrollEl.removeEventListener("scroll", el.__infiniteScroll.handler);
			delete el.__infiniteScroll;
		}
	}
}
